/**
 * Field-type registry for the digital-form builder + renderer.
 *
 * The same TypeScript shapes describe both:
 *   * What admins build in the FormBuilder (a list of FormField defs)
 *   * What the FormRenderer ingests to render employee-facing inputs
 *
 * Schema is stored as JSONB on ``document_templates.form_schema``;
 * server-side validation is permissive (open-ended ``list[Any]``)
 * so adding a new field type is a frontend-only change.
 */

export const FIELD_TYPES = [
  "text",
  "textarea",
  "date",
  "select",
  "checkbox",
  "checkbox_group",
  "radio",
  "phone",
  "email",
  "address",
  "ssn",
  "signature",
  "file_upload",
  "section_header",
] as const;

export type FieldType = (typeof FIELD_TYPES)[number];

export interface FormField {
  /** Stable id; the renderer uses this as the key into form_data. */
  id: string;
  type: FieldType;
  label: string;
  required?: boolean;
  help_text?: string;
  /** select / checkbox_group / radio. */
  options?: string[];
  /** Reserved for conditional visibility ("show this field when X = Y").
   *  Not enforced by v1 but reserved on the schema so we don't have
   *  to migrate later. */
  visible_if?: { field_id: string; equals: string };
}

export const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text: "Text — single line",
  textarea: "Text — multi-line",
  date: "Date",
  select: "Dropdown",
  checkbox: "Checkbox (single)",
  checkbox_group: "Checkbox group",
  radio: "Radio buttons",
  phone: "Phone number",
  email: "Email",
  address: "Address (street / city / state / zip)",
  ssn: "SSN (encrypted, masked after save)",
  signature: "Signature",
  file_upload: "File upload",
  section_header: "Section header (visual divider)",
};

/** Field types that need an admin-defined option list. */
export const FIELDS_WITH_OPTIONS: ReadonlySet<FieldType> = new Set([
  "select",
  "checkbox_group",
  "radio",
]);

let _idCounter = 0;
export function newFieldId(): string {
  _idCounter += 1;
  // Deterministic enough for client-side use; the server only reads
  // ``id`` as a key, never as a UUID.
  return `field_${Date.now().toString(36)}_${_idCounter}`;
}

/** Empty form_data factory keyed by id, with type-appropriate defaults. */
export function emptyFormData(schema: FormField[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of schema) {
    if (f.type === "section_header") continue;
    if (f.type === "checkbox") out[f.id] = false;
    else if (f.type === "checkbox_group") out[f.id] = [];
    else if (f.type === "address") out[f.id] = { street: "", city: "", state: "", zip: "" };
    else out[f.id] = "";
  }
  return out;
}

/** Light client-side validation — server is the source of truth. */
export function validateFormData(
  schema: FormField[],
  data: Record<string, unknown>,
): { ok: true } | { ok: false; firstError: string } {
  for (const f of schema) {
    if (f.type === "section_header") continue;
    if (!f.required) continue;
    const v = data[f.id];
    const empty =
      v === undefined ||
      v === null ||
      v === "" ||
      (Array.isArray(v) && v.length === 0) ||
      (f.type === "address" &&
        typeof v === "object" &&
        v !== null &&
        !Object.values(v as Record<string, string>).some((s) => s));
    if (empty) {
      return {
        ok: false,
        firstError: `${f.label || f.id} is required.`,
      };
    }
  }
  return { ok: true };
}
